import React, {useState} from 'react';
import {downloadCSV, ExportButton, List, useRedirect, useListContext, useTranslate, usePermissions, useRefresh} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import {Avatar, Box, Typography} from '@mui/material';
import {t} from '../themes/statamicTokens';
import {FilterDropdown} from '../components/statamic/FilterBar';
import StatamicListPage from '../components/statamic/StatamicListPage';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import hasPermission from '../auth/hasPermission';

const formatDate = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const formatDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

// Sponsors CSV export, same columns as prod's GuardianList (98adc045). Roles with
// guardians.export_details (admin, umbrella) get the full details; the others get the short set,
// without the surname the charitad routes never return.
const DETAIL_HEADERS = [
    ['id', 'ID'], ['salutation', 'Salutation'], ['given_name', 'First name'], ['family_name', 'Last name'],
    ['email', 'email'], ['street', 'Street'], ['house_number', 'House number'], ['postal_code', 'Postal code'],
    ['city', 'City'], ['country', 'Country'], ['sponsor_as_company', 'Sponsor as company'], ['company', 'Company'],
    ['donation_method', 'Donation method'], ['iban', 'IBAN'], ['direct_debit_reference', 'Direct debit reference'],
    ['direct_debit_start_date', 'Direct debit start date'], ['phone', 'Phone'], ['sponsor_children', 'Guardian of'],
    ['last_access_date_time', 'Last access date'], ['create_date_time', 'Registration date'], ['enabled', 'Enabled'],
];
const SHORT_HEADERS = [
    ['salutation', 'Salutation'], ['given_name', 'First name'], ['country', 'Country'], ['sponsor_children', 'Guardian of'], ['last_access_date_time', 'Last access date'],
    ['create_date_time', 'Registration date'], ['enabled', 'Enabled'],
];

export const guardianExportOptions = (dataset, detailed, translate = (s) => s) => {
    const columns = detailed ? DETAIL_HEADERS : SHORT_HEADERS;
    const headers = columns.map(([field]) => field);
    // Only the exported columns, so nothing outside the header list leaks into the CSV.
    const rows = dataset.map((item) => Object.fromEntries(headers.map((field) => [field, item[field] ?? ''])));
    const rename = columns.map(([field, label]) => (field === 'id' || field === 'iban' ? label : translate(label)));
    return {rows, headers, rename};
};

const makeGuardianExporter = (permissions, translate) => (dataset) => {
    const detailed = Boolean(hasPermission(permissions, ['export_details'], 'guardians'));
    const {rows, headers, rename} = guardianExportOptions(dataset, detailed, translate);
    jsonExport(rows, {headers, rename}, (err, csv) => {
        downloadCSV(csv, 'sponsoren');
    });
};

const SponsorCell = ({record, translate}) => {
    const avatarUrl = record.avatar
        ? `${process.env.REACT_APP_CDN_URL}/${record.avatar}`
        : undefined;

    const fullName = [
        record.salutation ? translate(record.salutation) : null,
        record.given_name,
        record.family_name,
    ].filter(Boolean).join(' ') || 'Unnamed';

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <Avatar
                src={avatarUrl}
                sx={{
                    width: 36,
                    height: 36,
                    bgcolor: t.color.sidebarHover,
                    color: t.color.text.secondary,
                    border: `1px solid ${t.color.border.hairline}`,
                    fontSize: 13,
                }}
            >
                {(record.given_name?.charAt(0) ?? '') + (record.family_name?.charAt(0) ?? '')}
            </Avatar>
            <Box sx={{minWidth: 0, display: 'flex', alignItems: 'center', gap: 1}}>
                <StatusDot variant={record.enabled ? 'success' : 'neutral'}/>
                <Typography
                    sx={{
                        fontSize: `${t.type.bodyStrong.size}px`,
                        fontWeight: t.type.bodyStrong.weight,
                        color: t.color.text.primary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {fullName}
                </Typography>
                {record.sponsor_children?.length > 0 && (
                    <StatusPill variant="success">Sponsor</StatusPill>
                )}
                {record.sponsored_child_count_in_scope === 0 && (
                    <StatusPill variant="neutral">{translate('Past sponsor')}</StatusPill>
                )}
            </Box>
        </Box>
    );
};

const GuardianListStatamicInner = () => {
    const redirect = useRedirect();
    const refresh = useRefresh();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const [activeViewId, setActiveViewId] = useState('enabled');
    const [deleteRecord, setDeleteRecord] = useState(null);

    const canExportDetails = hasPermission(permissions, ['export_details'], 'guardians');
    // Charity roles may only list and show sponsors; they have no edit route.
    const canEdit = hasPermission(permissions, ['edit'], 'guardians');

    const views = [
        {id: 'enabled',  label: 'Enabled'},
        {id: 'disabled', label: 'Disabled'},
        {id: 'all',      label: 'All'},
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        const base = {...filterValues};
        delete base.enabled;
        if (id === 'enabled')       setFilters({...base, enabled: true}, displayedFilters);
        else if (id === 'disabled') setFilters({...base, enabled: false}, displayedFilters);
        else                        setFilters(base, displayedFilters);
    };

    // Plan §2.3/§3 (D5): a past sponsor (sponsored_child_count_in_scope === 0) is
    // read-only — PUT and resetPassword 404 server-side until an attach gives them a
    // current child again. Hide Edit here rather than letting the row lead to a 404;
    // the field is UMBRELLA_ADMIN-only and absent for NOFAM_ADMIN, so this never hides
    // Edit for an admin.
    const isPastSponsor = (record) => record.sponsored_child_count_in_scope === 0;

    const rowActions = (record) => [
        {label: 'View',   onClick: () => redirect('show', 'guardians', record.id)},
        ...(!canEdit || isPastSponsor(record) ? [] : [{label: 'Edit', onClick: () => redirect('edit', 'guardians', record.id)}]),
        ...(permissions === 'admin' ? [{label: 'Delete', variant: 'danger', onClick: () => setDeleteRecord(record)}] : []),
    ];

    const columns = [
        {
            // The charitad routes have no surname and refuse to sort on it.
            key: 'sponsor', label: 'Sponsor', sortable: true, sortField: canExportDetails ? 'family_name' : 'given_name',
            render: (r) => <SponsorCell record={r} translate={translate}/>,
        },
        {
            key: 'location', label: 'Location', width: 200,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                    {[r.city, r.country].filter(Boolean).join(', ') || '—'}
                </Typography>
            ),
        },
        ...(canExportDetails ? [{
            key: 'payment', label: 'Payment', width: 120,
            render: (r) => r.donation_method
                ? <StatusPill variant="neutral">{String(r.donation_method).toUpperCase()}</StatusPill>
                : <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>—</Typography>,
        }] : []),
        {
            key: 'registered', label: 'Registered', sortable: true, sortField: 'create_date_time', width: 130,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                    {formatDate(r.create_date_time) ?? '—'}
                </Typography>
            ),
        },
        {
            key: 'lastseen', label: 'Last access', width: 160,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.secondary}}>
                    {formatDateTime(r.last_access_date_time) ?? '—'}
                </Typography>
            ),
        },
        ...(canExportDetails ? [{
            key: 'ddref', label: 'Direct debit ref.', width: 160,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontFamily: t.font.mono}}>
                    {r.direct_debit_reference || '—'}
                </Typography>
            ),
        }] : []),
    ];

    const updateFilter = (src, v) => setFilters({...filterValues, [src]: v}, displayedFilters);
    const clearFilter = (src) => {
        const next = {...filterValues};
        delete next[src];
        setFilters(next, displayedFilters);
    };

    return (
        <>
        <StatamicListPage
            scoped="v2People"
            title="Sponsors"
            subtitle="All guardians across the platform"
            /*
             * D11 — the "Create sponsor" header action was removed here. It opened
             * /guardians/create with no child, where Save is unconditionally disabled and the
             * server 400s on the missing child_external_id, so it had never led to a successful
             * save by any client. After W-03 it would also have rendered a sponsor search offering
             * to attach a child that does not exist. A sponsor is created from a child's screen,
             * which is the only flow the API supports.
             */
            views={views}
            activeView={activeViewId}
            onViewSelect={handleViewSelect}
            searchPlaceholder="Search sponsors"
            headerActions={<ExportButton label={translate('Export')}/>}
            filters={
                <>
                    <FilterDropdown
                        label="Enabled"
                        source="enabled"
                        type="boolean"
                        value={filterValues?.enabled}
                        onChange={updateFilter}
                        onClear={clearFilter}
                    />
                    {canExportDetails && (
                        <FilterDropdown
                            label="Payment method"
                            source="donation_method"
                            options={[
                                {id: 'iban',   name: 'IBAN'},
                                {id: 'paypal', name: 'PayPal'},
                                {id: 'stripe', name: 'Stripe'},
                                {id: 'other',  name: 'Other'},
                            ]}
                            value={filterValues?.donation_method}
                            onChange={updateFilter}
                            onClear={clearFilter}
                        />
                    )}
                </>
            }
            columns={columns}
            rowActions={rowActions}
            onRowClick={(record) => redirect('show', 'guardians', record.id)}
        />
        {deleteRecord && (
            <DeleteConfirmDialog
                open
                onClose={() => setDeleteRecord(null)}
                resource="guardians"
                record={deleteRecord}
                recordLabel={[deleteRecord.given_name, deleteRecord.family_name].filter(Boolean).join(' ') || undefined}
                onSuccess={refresh}
            />
        )}
        </>
    );
};

const GuardianListStatamic = (props) => {
    const {permissions} = usePermissions();
    const translate = useTranslate();
    return (
    <List
        {...props}
        exporter={makeGuardianExporter(permissions, translate)}
        sort={{field: 'create_date_time', order: 'DESC'}}
        perPage={25}
        filterDefaultValues={{enabled: true}}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <GuardianListStatamicInner/>
    </List>
    );
};

export default GuardianListStatamic;
